import { useEffect, useMemo, useState } from "react";
import { opsAPI, MODULE_META, fmtDT, PRIORITY_STYLE } from "./opsApi";
import { Search, Clock } from "lucide-react";

export default function OpsTimeline() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [moduleF, setModuleF] = useState("");
  const [priority, setPriority] = useState("");

  useEffect(() => {
    opsAPI.feed({
      module:   moduleF || undefined,
      priority: priority || undefined,
      limit:    300,
    }).then(setRows);
  }, [moduleF, priority]);

  const filtered = useMemo(() =>
    q ? rows.filter(e =>
      (e.title||"").toLowerCase().includes(q.toLowerCase()) ||
      (e.description||"").toLowerCase().includes(q.toLowerCase()) ||
      (e.entity_ref||"").toLowerCase().includes(q.toLowerCase())
    ) : rows
  , [rows, q]);

  // Group by hour of day within business date bucket
  const groups = useMemo(() => {
    const g = {};
    filtered.forEach(e => {
      try {
        const d = new Date(e.at);
        const key = d.toLocaleString([], { month:"short", day:"numeric" });
        (g[key] = g[key] || []).push(e);
      } catch { /* ignore */ }
    });
    return g;
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="ops-timeline">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search timeline…" aria-label="Search timeline"
                 data-testid="ops-timeline-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <select value={moduleF} onChange={e=>setModuleF(e.target.value)} data-testid="ops-timeline-module"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All modules</option>
          {Object.entries(MODULE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={priority} onChange={e=>setPriority(e.target.value)} data-testid="ops-timeline-priority"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">Any priority</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        {Object.keys(groups).length === 0
          ? <div className="p-8 text-center text-[13px] text-slate-400">No events in the timeline.</div>
          : Object.entries(groups).map(([day, events]) => (
              <div key={day} className="mb-6" data-testid={`ops-tl-day-${day}`}>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-3">{day}</div>
                <div className="relative border-l-2 border-slate-200 pl-5 space-y-3">
                  {events.map(e => {
                    const mm = MODULE_META[e.module] || MODULE_META.sys;
                    return (
                      <div key={e.id} className="relative" data-testid={`ops-tl-item-${e.id}`}>
                        <div className={`absolute -left-[26px] top-1 w-3 h-3 rounded-full border-2 border-white ${
                          e.priority === "critical" ? "bg-rose-500" :
                          e.priority === "high" ? "bg-amber-500" :
                          "bg-hocp-navy"
                        }`}/>
                        <div className="flex items-baseline gap-2">
                          <span className="mono text-[11px] text-slate-500 w-20 shrink-0">{new Date(e.at).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}</span>
                          <span className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">{mm.label}</span>
                          <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-medium capitalize ${PRIORITY_STYLE[e.priority]}`}>{e.priority}</span>
                          <span className="text-[13px] font-medium text-hocp-navy">{e.title}</span>
                        </div>
                        {e.description && <div className="text-[12px] text-slate-500 ml-24 mt-0.5">{e.description}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
}
